import { ArrowRight, Waypoints } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { SectionCard } from "~/components/section-card";

import type { EntityRelationRow } from "~/features/entities/types";

interface EntityRelationListProps {
  title: string;
  relations: EntityRelationRow[];
  onSelectEntity: (id: string) => void;
}

export function EntityRelationList({ title, relations, onSelectEntity }: EntityRelationListProps) {
  const { t } = useTranslation();

  // One row per (relation, related entity) pair — mirrors V1's RelationList table.
  const rows = relations.flatMap((rel) =>
    rel.related.map((entity) => ({ key: `${rel.relationId}:${entity.id}`, relation: rel, entity })),
  );

  return (
    <SectionCard
      icon={Waypoints}
      title={title}
      contentClassName={rows.length === 0 ? undefined : "overflow-x-auto p-0"}
    >
      {rows.length === 0 ? (
        <p className="text-[13px] text-fg-3">{t("feature.common.noData")}</p>
      ) : (
        <table className="w-full min-w-max border-collapse text-[13px]">
          <thead>
            <tr className="bg-bg-subtle">
              <Th>{t("model.record.relation.modelName")}</Th>
              <Th>{t("model.category.modelName")}</Th>
              <Th>{t("ui.date")}</Th>
              <Th>{t("model.common.name")}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ key, relation, entity }) => (
              <tr key={key} className="border-b border-border-default align-top last:border-b-0">
                <td className="px-3.5 py-2.5">
                  <Link
                    to={`/relations/${relation.relationId}`}
                    aria-label={t("ui.detail")}
                    className="text-fg-link hover:underline"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </td>
                <td className="px-3.5 py-2.5 text-fg-1">
                  {relation.categories.length === 0 ? (
                    <span className="text-fg-3">—</span>
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      {relation.categories.map((c) => (
                        <span key={c.id} className="inline-flex items-center gap-1.5">
                          <Link
                            to={`/admin/categories/${c.id}`}
                            aria-label={t("ui.detail")}
                            className="text-fg-link hover:underline"
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Link>
                          {c.label}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="whitespace-nowrap px-3.5 py-2.5 tabular-nums text-fg-2">{relation.dateRange || "—"}</td>
                <td className="px-3.5 py-2.5">
                  <button
                    type="button"
                    onClick={() => onSelectEntity(entity.id)}
                    className="inline-flex items-center gap-1.5 text-left text-fg-link hover:underline"
                  >
                    <ArrowRight className="h-4 w-4 shrink-0" />
                    <span>{entity.displayName || entity.id}</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SectionCard>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="border-b border-border-default px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-fg-2">
      {children}
    </th>
  );
}
